import { Project } from '../types';

/**
 * Interface para o formato de arquivo .wf
 */
interface WorkflowFile {
  appSignature: string;
  version: string;
  timestamp: string;
  project: Project;
}

/**
 * Assinatura do aplicativo para validação de arquivos
 */
const APP_SIGNATURE = 'WORKFLOW_STUDIO';
const FILE_VERSION = '1.0';

/**
 * Salva o projeto no disco do usuário como arquivo .wf
 * @param projectData - Objeto completo do projeto (Project)
 * @returns Promise que resolve quando o download é iniciado
 */
export const saveProjectToDisk = (projectData: Project): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      const fallbackSheetId = projectData.estado_atual?.activeSheetId || `sheet_${Date.now()}`;
      const normalizedSheets = projectData.estado_atual?.sheets?.length
        ? projectData.estado_atual.sheets
        : [{ id: fallbackSheetId, name: 'MAPA 1', cards: projectData.estado_atual?.cards ?? [] }];
      const normalizedProject: Project = {
        ...projectData,
        estado_atual: {
          ...(projectData.estado_atual ?? {}),
          sheets: normalizedSheets,
          activeSheetId: projectData.estado_atual?.activeSheetId || normalizedSheets[0]?.id || fallbackSheetId,
          cards: projectData.estado_atual?.cards ?? normalizedSheets[0]?.cards ?? [],
          postIts: projectData.estado_atual?.postIts ?? [],
          actions: projectData.estado_atual?.actions ?? [],
        },
      };

      // Criar objeto com metadados de segurança
      const workflowFile: WorkflowFile = {
        appSignature: APP_SIGNATURE,
        version: FILE_VERSION,
        timestamp: new Date().toISOString(),
        project: normalizedProject,
      };

      // Converter para JSON com formatação
      const jsonContent = JSON.stringify(workflowFile, null, 2);

      // Criar Blob com tipo MIME apropriado
      const blob = new Blob([jsonContent], { type: 'application/json' });

      // Criar URL temporária para o Blob
      const url = URL.createObjectURL(blob);

      // Criar elemento <a> para download
      const link = document.createElement('a');
      link.href = url;
      
      // Nome do arquivo baseado no nome do projeto, sanitizado
      const sanitizedFileName = normalizedProject.nome_projeto
        .replace(/[^a-z0-9]/gi, '_')
        .replace(/_+/g, '_')
        .toLowerCase() || 'projeto_sem_nome';
      
      link.download = `${sanitizedFileName}.wf`;
      
      // Adicionar ao DOM, clicar e remover
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Limpar URL temporária após um delay
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 100);

      resolve();
    } catch (error) {
      console.error('Erro ao salvar projeto:', error);
      reject(new Error('Falha ao salvar o projeto no disco'));
    }
  });
};

/**
 * Carrega um projeto do disco a partir de um arquivo .wf
 * @param file - Arquivo File selecionado pelo usuário
 * @returns Promise que resolve com os dados do projeto
 */
export const loadProjectFromDisk = (file: File): Promise<Project> => {
  return new Promise((resolve, reject) => {
    // Validar extensão do arquivo
    if (!file.name.toLowerCase().endsWith('.wf')) {
      reject(new Error('O arquivo deve ter a extensão .wf'));
      return;
    }

    // Criar FileReader
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        
        if (!content) {
          reject(new Error('Arquivo vazio ou inválido'));
          return;
        }

        // Fazer parse do JSON
        const parsedFile: WorkflowFile | Project = JSON.parse(content);
        const workflowFile = parsedFile as Partial<WorkflowFile>;

        // Validar assinatura do aplicativo quando o arquivo usa envelope .wf moderno.
        // Arquivos legados podem conter o Project diretamente.
        if (workflowFile.appSignature && workflowFile.appSignature !== APP_SIGNATURE) {
          reject(new Error('Arquivo inválido: não é um arquivo WORKFLOW válido'));
          return;
        }

        // Validar versão (pode ser usado para migração futura)
        if (!workflowFile.version) {
          console.warn('Arquivo sem versão detectada, assumindo versão 1.0');
        }

        const project = workflowFile.project ?? (parsedFile as Project);

        // Validar se tem dados do projeto
        if (!project) {
          reject(new Error('Arquivo corrompido: dados do projeto não encontrados'));
          return;
        }

        // Validar estrutura básica do projeto
        if (!project.id || !project.nome_projeto) {
          reject(new Error('Arquivo corrompido: estrutura do projeto inválida'));
          return;
        }

        // Retornar dados do projeto
        resolve(project);
      } catch (error) {
        console.error('Erro ao carregar projeto:', error);
        if (error instanceof SyntaxError) {
          reject(new Error('Arquivo corrompido: formato JSON inválido'));
        } else if (error instanceof Error) {
          reject(error);
        } else {
          reject(new Error('Erro ao processar o arquivo'));
        }
      }
    };

    reader.onerror = () => {
      reject(new Error('Erro ao ler o arquivo'));
    };

    // Ler arquivo como texto
    reader.readAsText(file);
  });
};

/**
 * Valida se um arquivo é um arquivo .wf válido
 * @param file - Arquivo a ser validado
 * @returns true se o arquivo é válido
 */
export const isValidWorkflowFile = (file: File): boolean => {
  return file.name.toLowerCase().endsWith('.wf');
};
